import {
	type ResidenceListData,
	residenceGet,
	residenceGetDsuRate,
	residenceGetMailboxTestStatus,
	residenceList,
	residenceListManagers
} from '@repo/sdk/gen'
import { queryOptions } from '@tanstack/react-query'
import { makeQueryKeys } from '@/query/query-keys'

export function residencesListQueryOptions(body: ResidenceListData['body']) {
	return queryOptions({
		queryKey: makeQueryKeys.residences.index(body),
		queryFn: ({ signal }) => residenceList({ body, signal }),
		placeholderData: (prev) => prev
	})
}

/** The DSU rate in force for every residence; `data` is null until the general admin sets the first. */
export function residenceDsuRateQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.residences.dsuRate(),
		queryFn: ({ signal }) => residenceGetDsuRate({ signal })
	})
}

/** One residence by id; null when it does not exist. */
export function residenceDetailQueryOptions(residenceId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.residences.detail(residenceId),
		queryFn: async ({ signal }) => {
			const response = await residenceGet({
				body: { filters: { ids: [residenceId] } },
				signal
			})
			return response.data[0] ?? null
		}
	})
}

/** The facility manager accounts of a residence, oldest first. */
export function residenceManagersQueryOptions(residenceId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.residences.managers(residenceId),
		queryFn: ({ signal }) =>
			residenceListManagers({
				body: { filters: { residenceId } },
				signal
			})
	})
}

/**
 * The test email of the PEC of a residence. While the mailbox has not answered
 * yet, it is read again every few seconds.
 */
export function residenceMailboxTestQueryOptions(residenceId: string) {
	return queryOptions({
		queryKey: makeQueryKeys.residences.mailboxTest(residenceId),
		queryFn: ({ signal }) =>
			residenceGetMailboxTestStatus({
				body: { filters: { residenceId } },
				signal
			}),
		refetchInterval: (query) =>
			query.state.data?.data?.delivery === 'sending' ? 5000 : false
	})
}
